import {
    Box,
    Flex,
    Heading,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import BackButton from "components/stepper/back-button";
import StepperSkeleton from "components/stepper/stepper-skeleton";
import { ActiveStep, useActiveStep } from "components/stepper/use-active-step";
import { useBookingNavigate } from "components/stepper/use-booking-navigate";
import { useBookingStateContext } from "hooks/use-booking-state";
import { usePaymentRule } from "hooks/use-payment-rule";
import i18n from "i18next";
import { LayoutContext } from "layout/layout-context";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import { Routes } from "routes";
import { getSearchParams } from "search-params";
import {
    StepperModalModeNegativeMarginTop,
    StepperModalModePaddingTop,
    StepperModalModeStickyTop,
} from "style-variables";
import { AppMode } from "types/app-mode";
import { PaymentRule } from "types/payment";

const Stepper = () => {
    const externalBookingSettings = useExternalBookingSettings();
    const state = useBookingStateContext();
    const { t } = useTranslation();
    const activeStep = useActiveStep();
    const paymentRule = usePaymentRule();
    const { bookingNavigate } = useBookingNavigate();
    const navigate = useNavigate();
    const { appMode } = useContext(LayoutContext);
    const { data: booking } = useWebBooking();

    if (externalBookingSettings.isLoading) return <StepperSkeleton />;

    const { activateWebChildren: hasActivatedChildrenStep } =
        externalBookingSettings.data?.unitsMetaData || {};
    const { guests, guestsChildren, date, isActivityBooking } = state;

    const dateText = date?.locale(i18n.language).format("D MMM YYYY");
    let guestText = t("start.numberGuests", { guests });

    if (guestsChildren) {
        const childrenText = t("start.ofWhich", { children: guestsChildren });
        guestText = `${guestText} ${childrenText}`;
    }

    let requestGuestText = "";
    if (guests) {
        if (!hasActivatedChildrenStep || !guestsChildren) {
            requestGuestText = t("start.numberGuests", { guests });
        } else {
            const adults = guests - guestsChildren;
            requestGuestText =
                adults + " " + t("adults") + ", " + guestsChildren + " " + t("children");
        }
    }

    const getFinalStepTitle = (isRequestFlow: boolean, isPrepayment: boolean) => {
        if (isRequestFlow) {
            return t("stepper.confirmAndSendRequest");
        }
        return isPrepayment ? t("stepper.confirmDetailsAndPayment") : t("stepper.confirmDetails");
    };

    const isRequestFlow = location.pathname.includes(Routes.BOOKING_REQUEST);
    const isPrepayment = paymentRule === PaymentRule.Prepayment;
    const { isExperienceViewMode } = getSearchParams();
    const stepContext = [
        {
            condition: ActiveStep.Guests,
            summaryText: [isRequestFlow ? t("stepper.bookingRequest") : t("stepper.welcome")],
            stepTitle: t("stepper.chooseGuestSize"),
            hideBackButton: !isRequestFlow,
            navigateBack: () => {
                if (isRequestFlow) {
                    const path = location.pathname.replace(
                        `/${Routes.BOOKING_REQUEST}`,
                        isExperienceViewMode ? `/${Routes.EXPERIENCE_VIEW}` : ""
                    );

                    void navigate({ pathname: path, search: location.search });
                }
            },
        },
        {
            condition: ActiveStep.GuestsChildren,
            summaryText: [guestText],
            stepTitle: externalBookingSettings.data?.unitsMetaData.childrenAge
                ? t("stepper.chooseGuestChildrenOfAge", {
                      maxAge: externalBookingSettings.data?.unitsMetaData.childrenAge,
                  })
                : t("stepper.chooseGuestChildren"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.WIZARD, back: true }),
        },
        {
            condition: ActiveStep.Date,
            summaryText: [isRequestFlow ? requestGuestText : guestText],
            stepTitle: t("stepper.chooseDate"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.WIZARD, back: true }),
        },
        {
            condition: ActiveStep.Availability,
            summaryText: [isRequestFlow ? requestGuestText : guestText, dateText],
            stepTitle: isActivityBooking ? t("stepper.chooseActivities") : t("stepper.chooseTime"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.AVAILABILITY, back: true }),
        },
        {
            condition: ActiveStep.Preorder,
            summaryText: [isRequestFlow ? requestGuestText : guestText, dateText],
            stepTitle: t("stepper.preorder"),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.PREORDER, back: true }),
        },
        {
            condition: ActiveStep.Finalize,
            summaryText: [isRequestFlow ? requestGuestText : guestText, dateText],
            stepTitle: getFinalStepTitle(isRequestFlow, isPrepayment),
            navigateBack: () => bookingNavigate({ fromRoute: Routes.FINALIZE, back: true }),
        },
    ];

    const progressbarHeight = "0.25rem";
    const stepTitle = stepContext.find((step) => step.condition === activeStep)?.stepTitle || "";
    const stickyStepper = appMode === AppMode.Modal || appMode === AppMode.Standalone;
    const useModalModePositioning = appMode === AppMode.Modal;

    let steps = ["Guests", "GuestsChildren", "Date", "Availability", "Preorder", "Finalize"];

    if (!hasActivatedChildrenStep || isRequestFlow) {
        steps = steps.filter((step) => step !== "GuestsChildren");
    }
    if (
        isActivityBooking ||
        (!booking?.hasMenus() && !location.pathname.includes(Routes.PREORDER))
    ) {
        steps = steps.filter((step) => step !== "Preorder");
    }
    const activeStepRelcalculated = steps.indexOf(ActiveStep[activeStep]);
    return (
        <Box
            role="navigation"
            aria-label={t("stepper.navigationWithCurrentStep", {
                stepTitle,
                current: activeStepRelcalculated + 1,
                total: steps.length,
            })}
            display="flex"
            flexDirection="column"
            mt={useModalModePositioning ? StepperModalModeNegativeMarginTop : 0}
            pt={useModalModePositioning ? StepperModalModePaddingTop : ThemeSpaceVariable.Medium}
            px={ThemeSpaceVariable.Medium}
            pb={ThemeSpaceVariable.Medium}
            position={stickyStepper ? "sticky" : "static"}
            top={useModalModePositioning ? StepperModalModeStickyTop : 0}
            bg={ThemeColorVariable.Background}
            zIndex={2}
        >
            <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Box
                    flex={1}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={ThemeSpaceVariable.XSmall}
                    fontSize={ThemeFontSizeVariable.Medium}
                >
                    {stepContext.map((step) => {
                        if (step.condition === activeStep) {
                            return (
                                <Flex
                                    key={step.condition}
                                    w="full"
                                    textAlign="center"
                                    align="center"
                                >
                                    {step.hideBackButton ? (
                                        <Flex flex={1} justifyContent="flex-start" />
                                    ) : (
                                        <Flex flex={1} align="center">
                                            <BackButton
                                                onClick={() => {
                                                    void step.navigateBack();
                                                }}
                                            />
                                        </Flex>
                                    )}

                                    <Flex direction="column" align="center" flex={3}>
                                        <Flex
                                            justify="center"
                                            direction="row"
                                            gap={ThemeSpaceVariable.XSmall}
                                            flexWrap="wrap"
                                            align="center"
                                            color={ThemeColorVariable.OnBackgroundSubdued}
                                        >
                                            {step.summaryText.map((text, index) => (
                                                <Fragment key={index}>
                                                    <Text
                                                        key={index}
                                                        as="span"
                                                        fontSize={ThemeFontSizeVariable.Small}
                                                        color={
                                                            ThemeColorVariable.OnBackgroundSubdued
                                                        }
                                                    >
                                                        {text}
                                                        {index !== step.summaryText.length - 1 &&
                                                            ","}
                                                    </Text>
                                                </Fragment>
                                            ))}
                                        </Flex>

                                        <Heading
                                            as="h2"
                                            fontWeight="bold"
                                            fontSize={ThemeFontSizeVariable.Medium}
                                            lineHeight={1.5}
                                        >
                                            {step.stepTitle}
                                        </Heading>
                                    </Flex>
                                    <Flex flex="1" />
                                </Flex>
                            );
                        }
                        return null;
                    })}
                </Box>
            </Flex>

            <Box
                position="relative"
                width="full"
                height={progressbarHeight}
                mt={ThemeSpaceVariable.Small}
                overflow="hidden"
                role="group"
                aria-label={t("stepper.stepIndicator", {
                    current: activeStepRelcalculated + 1,
                    total: steps.length,
                })}
                display="flex"
                gap={ThemeSpaceVariable.Small}
            >
                {Array.from({ length: steps.length }).map((_, index) => {
                    return (
                        <Box
                            key={index}
                            borderRadius={ThemeRadiusVariable.XSmall}
                            w="full"
                            bg={
                                index < Number(activeStepRelcalculated + 1)
                                    ? ThemeColorVariable.Secondary
                                    : ThemeColorVariable.BackgroundDisabled
                            }
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export { Stepper };
